'use client';

import type { ReactNode } from 'react';
import { cx } from 'class-variance-authority';
import { motion } from 'motion/react';
import Icon from './Icon';
import useBlockBodyScrolling from './hooks/useBlockBodyScrolling';
import Portal from './Portal';
import ModalFooter from './Modal/ModalFooter';
import ModalHeader from './Modal/ModalHeader';
import ModalContent from './Modal/ModalContent';
import ModalBackground from './Modal/ModalBackground';
import { cn } from '~/utils';
import { useEventListener } from './hooks/DOM';

type DrawerProps = {
    onClose: (...args: any) => any;
    className?: string;
    children: ReactNode;
    classNameDrawer?: string;
    /** className for the blurry background overlay */
    overlayClassName?: string;
    goBack?: (...args: any) => any;
    /** Where the panel slides in from. `bottom` is the phone sheet: it rises
     * from the bottom edge and shows a drag-handle bar.
     * @default 'left' */
    side?: 'left' | 'bottom';
};

export default function Drawer({
    onClose,
    goBack,
    children,
    className,
    classNameDrawer,
    overlayClassName,
    side = 'left',
}: DrawerProps) {
    useBlockBodyScrolling();
    useEventListener('window', 'keydown', e => {
        if (e.key === 'Escape') onClose?.();
    });

    return (
        <Portal querySelector="#modals">
            <ModalBackground onClick={onClose} className={overlayClassName} />
            <motion.div
                role="dialog"
                tabIndex={-1}
                aria-modal="true"
                animate={side === 'bottom' ? { y: 0 } : { x: 0 }}
                drag={side === 'bottom' ? 'y' : false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.6 }}
                onDragEnd={(_event, info) => {
                    if (side === 'bottom' && (info.offset.y > 120 || info.velocity.y > 600)) {
                        onClose();
                    }
                }}
                data-testid="Drawer"
                transition={{
                    bounce: 0.1,
                }}
                exit={side === 'bottom' ? { y: '100%' } : { x: '-200%' }}
                initial={side === 'bottom' ? { y: '100%' } : { x: '-200%' }}
                className={cx(
                    'fixed z-modals flex min-h-0 flex-col overflow-hidden shadow-md',
                    side === 'bottom'
                        ? 'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-4xl'
                        : 'inset-y-0 left-0 rounded-r-xl',
                    classNameDrawer,
                )}
            >
                <div
                    className={cn(
                        'relative flex min-h-0 flex-1 flex-col overflow-hidden',
                        side === 'left' && 'min-w-96',
                        className,
                    )}
                >
                    {side === 'bottom' && (
                        <div aria-hidden className="flex shrink-0 justify-center pt-3">
                            <span className="h-1.5 w-16 rounded-full bg-gray-200" />
                        </div>
                    )}
                    {goBack && (
                        <div
                            onClick={goBack}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    onClose();
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            className="absolute left-2 top-3"
                        >
                            <Icon name="arrow-left" className="m-4 h-6 w-6" />
                        </div>
                    )}
                    {children}
                </div>
            </motion.div>
        </Portal>
    );
}

Drawer.Footer = ModalFooter;
Drawer.Header = ModalHeader;
Drawer.Content = ModalContent;
